import API from "./api";

export interface GstCredential {
  id: string;
  gstin: string;
  gstUsername: string;
  gstLegalName?: string;
  gstState?: string;
  fetchedFromPan?: boolean;
  sessionStatus: string;
  tokenExpiresAt: string | null;
  lastAuthenticatedAt: string | null;
  needsReauth: boolean;
}

export interface DashboardKpis {
  totalSpend: number;
  totalVendors: number;
  totalInvoices: number;
  itcSecured: number;
  itcDelayedRecovered: number;
  itcAtRisk: number;
  itcLost: number;
  itcMismatch: number;
  itcEntitlement?: number;
  topVendorConcentrationPct?: number;
}

export interface DashboardMonthlyRow {
  month: string;
  spend: number;
  itcSecured: number;
  itcAtRisk: number;
  itcLost: number;
  itcDelayedRecovered: number;
  booksNot2b: number;
  in2bNotBooks: number;
  itcMismatch: number;
}

export interface DashboardVendorRow {
  supplierGstin: string;
  supplierName: string;
  totalSpend: number;
  invoiceCount: number;
  itcSecured: number;
  itcAtRisk: number;
  itcLost: number;
  itcDelayedRecovered: number;
  itcMismatch: number;
  internalTransferCount: number;
  avgFilingDay: number | null;
  filingFrequency: "Monthly" | "Quarterly" | null;
}

export interface DashboardInvoiceRow {
  reconLineId: string;
  gstin: string;
  period: string;
  supplierGstin: string;
  supplierName: string;
  matchStatus: string;
  claimStatus: string;
  isInternalTransfer: boolean;
  purchaseInvoiceId: string | null;
  gstr2bInvoiceId: string | null;
  purchaseInvoiceNumber: string | null;
  gstr2bInvoiceNumber: string | null;
  purchaseInvoiceDate: string | null;
  gstr2bInvoiceDate: string | null;
  purchaseBuyerGstin?: string | null;
  gstr2bBuyerGstin?: string | null;
  purchaseSupplierGstin: string | null;   // purchase-side supplier GSTIN
  purchaseSupplierName: string | null;    // purchase-side supplier name
  purchaseTaxableValue: number;
  gstr2bTaxableValue: number;
  purchaseIgst: number;
  purchaseCgst: number;
  purchaseSgst: number;
  purchaseCess: number;
  purchaseTaxAmount: number;
  gstr2bIgst: number;
  gstr2bCgst: number;
  gstr2bSgst: number;
  gstr2bCess: number;
  gstr2bTaxAmount: number;
  purchaseInvoiceValue: number;
  gstr2bInvoiceValue: number;
  itcEligible: number;
  itcAtRisk: number;
  mismatchAmount: number;
  mismatchFields: Record<string, any> | null;
  actionLog: any[];
}

export interface DashboardInvoiceSummary {
  totalInvoices: number;
  needsInput: number;
  purchaseTaxAmount: number;
  gstr2bTaxAmount: number;
  gstAmountGap: number;
  internalTransferCount: number;
  byClaimStatus: Array<{ claimBucket: "claim_itc" | "pending" | "ineligible"; invoiceCount: number; taxAmount: number }>;
  byMatchStatus: Array<{ matchBucket: "matched" | "not_in_2b" | "not_in_pr" | "mismatch"; invoiceCount: number; taxAmount: number }>;
}

export interface DashboardInvoicesResponse {
  rows: DashboardInvoiceRow[];
  summary: DashboardInvoiceSummary;
  total: number;
}

export interface ManualMatchInvoiceRow {
  id: string;
  reconLineId: string;
  claimStatus: string;
  gstin: string;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  supplierName: string;
  supplierGstin: string;
  taxableValue: number;
  taxAmount: number;
  invoiceValue: number;
}

export type DashboardSortColumn =
  | "invoiceNumber"
  | "invoiceDate"
  | "supplierName"
  | "buyerGstin"
  | "taxableValue"
  | "gstAmount"
  | "matchStatus"
  | "claimStatus";

export interface DashboardFilters {
  gstin?: string | null;
  periodFrom: string;
  periodTo: string;
  search?: string;
  matchStatus?: string[];
  claimStatus?: string[];
  supplierName?: string[];
  buyerGstin?: string[];
  invoiceNumber?: string[];
  invoiceDate?: string[];
  taxableValue?: string[];
  gstAmount?: string[];
  supplierGstin?: string;
  filingFrequency?: string;
  needsActionOnly?: boolean;
  internalTransfersOnly?: boolean;
  sortBy?: DashboardSortColumn;
  sortDirection?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

export interface DashboardColumnDistinctValue {
  value: string;
  label?: string;
  count: number;
}

export type FileYearTag = "CURRENT_FY" | "PREVIOUS_FY";

export interface UploadedFile {
  id: string;
  fileId?: string;
  fileType: string;
  source: string;
  periodStart: string | null;
  periodEnd: string | null;
  originalFilename: string;
  status: string;
  parsedRowCount?: number;
  fileYearTag?: FileYearTag | null;
}

export interface ActiveJob {
  jobId: number;
  jobType: "sanity_check" | "run_recon" | "gst_fetch_data";
  status: "pending" | "running";
  gstin: string | null;
}

export interface AiRecommendation {
  id: string;
  title: string;
  body: string;
  category: string;
  priority: "high" | "med" | "low";
  next_step: string;
  type?: string;
}

export interface  GstInsightsData {
  fire_summary?: { headline: string; subtext: string };
  fire_actionables?: Array<{ icon: string; label: string; detail: string }>;
  ai_recommendations?: AiRecommendation[];
}

export interface DashboardOverviewData {
  filters: {
    gstin: string | null;
    periodFrom: string;
    periodTo: string;
    latestGstr2bPeriod: string | null;
  };
  kpis: DashboardKpis & { itcEntitlement: number; topVendorConcentrationPct: number };
  monthly: DashboardMonthlyRow[];
  vendors: DashboardVendorRow[];
  filingHistogram: number[];
  invoiceSummary: DashboardInvoiceSummary;
}

export interface GstAccessUser {
  userId: number;
  name: string;
  email: string;
  role: string;
  access: boolean;
}

export interface GstAccessData {
  users: GstAccessUser[];
  grantedBy: { userId: number; name: string; email: string } | null;
}

class GstRestService {
  // ── Auth ────────────────────────────────────────────────────────────────────

  generateOtp(gstin: string, gstUsername: string): Promise<any> {
    return new Promise((resolve, reject) => {
      API.post(`/gst/otp/generate`, { gstin, gstUsername })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  verifyOtp(gstin: string, gstUsername: string, otp: string): Promise<any> {
    return new Promise((resolve, reject) => {
      API.post(`/gst/otp/verify`, { gstin, gstUsername, otp })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  getCredentials(): Promise<any> {
    return new Promise((resolve, reject) => {
      API.get("/gst/credentials")
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  deleteCredential(credentialId: string, gstin?: string): Promise<any> {
    return new Promise((resolve, reject) => {
      API.delete("/gst/credentials", {
        data: { credentialId, ...(gstin && { gstin }) },
        headers: { "Content-Type": "application/json" },
      }).then((res) => resolve(res.data)).catch((err) => reject(err));
    });
  }

  getAuthStatus(gstin?: string): Promise<any> {
    return new Promise((resolve, reject) => {
      API.get("/gst/auth-status", { params: gstin ? { gstin } : {} })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  refreshSession(credentialId?: string, gstin?: string): Promise<any> {
    return new Promise((resolve, reject) => {
      API.post("/gst/session/refresh", { credentialId, gstin })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  // ── Fetch GSTR-2B via API ───────────────────────────────────────────────────

  fetchGstr2b(params: { credentialId?: string; gstin?: string; month?: string }): Promise<any> {
    return new Promise((resolve, reject) => {
      API.post("/gst/fetch/gstr2b", params)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  // ── Upload Purchase Register ────────────────────────────────────────────────

  uploadPurchases(source: string, files: File[], periodStart?: string, periodEnd?: string): Promise<any> {
    const formData = new FormData();
    formData.append("source", source);
    if (periodStart) formData.append("periodStart", periodStart);
    if (periodEnd) formData.append("periodEnd", periodEnd);
    files.forEach((file) => formData.append("files", file));
    return new Promise((resolve, reject) => {
      API.post("/gst/upload/purchases", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      }).then((res) => resolve(res.data)).catch((err) => reject(err));
    });
  }

  getPurchaseFiles(): Promise<{ files: UploadedFile[]; recordsTill: string | null }> {
    return new Promise((resolve, reject) => {
      API.get("/gst/upload/purchases").then((res) => {
        const response = res.data;
        const mapFile = (file: any): UploadedFile => ({ ...file, id: file.id ?? file.fileId });
        // New shape: { success, data: { files, recordsTill } }
        if (response?.success && response.data?.files && Array.isArray(response.data.files)) {
          resolve({
            files: response.data.files.map(mapFile),
            recordsTill: response.data.recordsTill ?? null,
          });
        }
        // Legacy fallback: { success, data: [...] }
        if (response?.success && Array.isArray(response.data)) {
          resolve({ files: response.data.map(mapFile), recordsTill: null });
        }
        resolve({ files: [], recordsTill: null });
      }).catch((err) => reject(err));
    });
  }

  // ── Upload GST credentials list ─────────────────────────────────────────────

  uploadCredentialsList(file: File): Promise<any> {
    const formData = new FormData();
    formData.append("files", file);
    return new Promise((resolve, reject) => {
      API.post("/gst/upload/credentials", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      }).then((res) => resolve(res.data)).catch((err) => reject(err));
    });
  }

  deletePurchaseFile(fileId: string, isDashboardDelete?: boolean): Promise<any> {
    return new Promise((resolve, reject) => {
      API.delete("/gst/upload/purchases", {
        data: { fileId, ...(isDashboardDelete && { isDashboardDelete: true }) },
        headers: { "Content-Type": "application/json" },
      }).then((res) => resolve(res.data)).catch((err) => reject(err));
    });
  }

  // ── Reconciliation ──────────────────────────────────────────────────────────

  runReconIntelligence(
    source: string,
    files: File[],
    isDashboardUpload?: boolean,
    fileYearTags?: (FileYearTag | null | undefined)[],
  ): Promise<any> {
    const formData = new FormData();
    formData.append("source", source);
    if (isDashboardUpload) formData.append("isDashboardUpload", "true");
    files.forEach((file, i) => {
      formData.append("files", file);
      // Keep fileYearTags aligned index-for-index with files when provided.
      if (fileYearTags) formData.append("fileYearTags", fileYearTags[i] ?? "");
    });
    return new Promise((resolve, reject) => {
      API.post("/gst/recon/runintelligence", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      }).then((res) => resolve(res.data)).catch((err) => reject(err));
    });
  }

  manualRunReconIntelligence(): Promise<any> {
    return new Promise((resolve, reject) => {
      API.post("/gst/manual/recon/runintelligence")
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  runRecon(gstin: string): Promise<any> {
    return new Promise((resolve, reject) => {
      API.post("/gst/recon/run", { gstin })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  manualMatch(
    purchaseInvoiceId: string,
    gstr2bInvoiceId: string,
    notes?: string
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      API.post("/gst/recon/manualmatch", {
        purchaseInvoiceId,
        gstr2bInvoiceId,
        notes,
      }).then((res) => resolve(res.data)).catch((err) => reject(err));
    });
  }

  manualUnmatch(reconLineId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      API.post("/gst/recon/manualunmatch", { reconLineId })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  // ── Dashboard ───────────────────────────────────────────────────────────────

  getDashboardOverview(filters: DashboardFilters): Promise<{ success: boolean; data?: DashboardOverviewData; message?: string }> {
    return new Promise((resolve, reject) => {
      API.get("/gst/dashboard/overview", { params: filters })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  getDashboardKpis(
    gstin: string | null,
    periodFrom: string,
    periodTo: string
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      API.get("/gst/dashboard/kpis", {
        params: { gstin, periodFrom, periodTo },
      }).then((res) => resolve(res.data)).catch((err) => reject(err));
    });
  }

  getDashboardMonthly(
    gstin: string | null,
    periodFrom: string,
    periodTo: string
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      API.get("/gst/dashboard/monthly", {
        params: { gstin, periodFrom, periodTo },
      }).then((res) => resolve(res.data)).catch((err) => reject(err));
    });
  }

  getDashboardInvoices(filters: DashboardFilters): Promise<any> {
    return new Promise((resolve, reject) => {
      API.get("/gst/dashboard/invoices", { params: filters })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  getDashboardColumnValues(params: {
    column: DashboardSortColumn;
    gstin: string | null;
    periodFrom: string;
    periodTo: string;
    search?: string;
    matchStatus?: string[];
    claimStatus?: string[];
    supplierName?: string[];
    buyerGstin?: string[];
    invoiceNumber?: string[];
    invoiceDate?: string[];
    taxableValue?: string[];
    gstAmount?: string[];
    valueSearch?: string;
    limit?: number;
  }): Promise<{ success: boolean; data?: { values: DashboardColumnDistinctValue[]; truncated: boolean }; message?: string }> {
    return new Promise((resolve, reject) => {
      API.get("/gst/dashboard/invoices/column-values", { params })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  getDashboardVendors(
    gstin: string | null,
    periodFrom: string,
    periodTo: string
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      API.get("/gst/dashboard/vendors", {
        params: { gstin, periodFrom, periodTo },
      }).then((res) => resolve(res.data)).catch((err) => reject(err));
    });
  }

  getDashboardFilingHistogram(
    gstin: string | null,
    periodFrom: string,
    periodTo: string
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      API.get("/gst/dashboard/filing-histogram", {
        params: { gstin, periodFrom, periodTo },
      }).then((res) => resolve(res.data)).catch((err) => reject(err));
    });
  }

  getManualMatchQueue(filters: DashboardFilters): Promise<any> {
    return new Promise((resolve, reject) => {
      API.get("/gst/recon/manualmatch/queue", { params: filters })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  updateInvoiceClaimStatus(
    reconLineId: string,
    claimStatus: string,
    note?: string
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      API.patch("/gst/dashboard/invoices/claim-status", {
        reconLineId,
        claimStatus,
        note,
      }).then((res) => resolve(res.data)).catch((err) => reject(err));
    });
  }

  bulkUpdateClaimStatus(
    reconLineIds: string[],
    claimStatus: string
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      API.patch("/gst/dashboard/invoices/claim-status", {
        reconLineIds,
        claimStatus,
      }).then((res) => resolve(res.data)).catch((err) => reject(err));
    });
  }

  addInvoiceNote(reconLineId: string, note: string): Promise<any> {
    return new Promise((resolve, reject) => {
      API.post("/gst/dashboard/invoices/note", { reconLineId, note })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  getGstDetails(gstin: string): Promise<any> {
    return new Promise((resolve, reject) => {
      API.get("/gst/details", { params: { gstin } }).then((res) => resolve(res.data)).catch((err) => reject(err));
    });
  }

  // ── Access control ──────────────────────────────────────────────────────────

  getAccess(): Promise<{ success: boolean; data: GstAccessData }> {
    return new Promise((resolve, reject) => {
      API.get("/gst/access").then((res) => resolve(res.data)).catch((err) => reject(err));
    });
  }

  grantAccess(userId: number, access: boolean): Promise<any> {
    return new Promise((resolve, reject) => {
      API.post("/gst/access/grant", { userId, access }).then((res) => resolve(res.data)).catch((err) => reject(err));
    });
  }

  getInsights(gstin: string | null): Promise<{ success: boolean; data: GstInsightsData | null }> {
    const params: Record<string, string> = {};
    if (gstin) params.gstin = gstin;
    return new Promise((resolve, reject) => {
      API.get("/gst/insights", { params }).then((res) => resolve(res.data)).catch((err) => reject(err));
    });
  }

  getJobStatus(): Promise<{ success: boolean; data: { jobs: ActiveJob[]; reconCompleted: boolean; nextReconExecution: number; reconLoading: boolean } }> {
    return new Promise((resolve, reject) => {
      API.get("/gst/jobs/status").then((res) => resolve(res.data)).catch((err) => reject(err));
    });
  }

  downloadReport(
    type: "invoice_reconciliation",
    filters: Pick<
      DashboardFilters,
      | "gstin" | "periodFrom" | "periodTo" | "search"
      | "matchStatus" | "claimStatus" | "supplierName" | "buyerGstin"
      | "invoiceNumber" | "invoiceDate" | "taxableValue" | "gstAmount"
      | "sortBy" | "sortDirection" | "internalTransfersOnly"
    >
  ): Promise<Blob> {
    // Normalize to YYYY-MM-DD — if only YYYY-MM is passed, pad to first/last day
    const normalizeDate = (d: string, isTo: boolean): string => {
      if (/^\d{4}-\d{2}$/.test(d)) {
        if (!isTo) return `${d}-01`;
        const [y, m] = d.split("-").map(Number);
        const lastDay = new Date(y, m, 0).getDate();
        return `${d}-${String(lastDay).padStart(2, "0")}`;
      }
      return d;
    };
    const params: Record<string, any> = {
      type,
      periodFrom: normalizeDate(filters.periodFrom, false),
      periodTo: normalizeDate(filters.periodTo, true),
    };
    if (filters.gstin) params.gstin = filters.gstin;
    if (filters.search) params.search = filters.search;
    if (filters.matchStatus) params.matchStatus = filters.matchStatus;
    if (filters.claimStatus) params.claimStatus = filters.claimStatus;
    if (filters.supplierName) params.supplierName = filters.supplierName;
    if (filters.buyerGstin) params.buyerGstin = filters.buyerGstin;
    if (filters.invoiceNumber) params.invoiceNumber = filters.invoiceNumber;
    if (filters.invoiceDate) params.invoiceDate = filters.invoiceDate;
    if (filters.taxableValue) params.taxableValue = filters.taxableValue;
    if (filters.gstAmount) params.gstAmount = filters.gstAmount;
    if (filters.sortBy) params.sortBy = filters.sortBy;
    if (filters.sortDirection) params.sortDirection = filters.sortDirection;
    if (filters.internalTransfersOnly) params.internalTransfersOnly = filters.internalTransfersOnly;
    return new Promise((resolve, reject) => {
      API.get("/gst/dashboard/export", { params, responseType: "blob" })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  downloadSupplierTracker(
    filters: Pick<DashboardFilters, "gstin" | "periodFrom" | "periodTo">
  ): Promise<Blob> {
    const normalizeDate = (d: string, isTo: boolean): string => {
      const m = /^(\d{4})-(\d{2})$/.exec(d);
      if (!m) return d;
      const [, year, month] = m;
      if (!isTo) return `${d}-01`;
      // day 0 of the next month === last day of this month
      const lastDay = new Date(Number(year), Number(month), 0).getDate();
      return `${d}-${String(lastDay).padStart(2, "0")}`;
    };
    const params: Record<string, any> = {
      periodFrom: normalizeDate(filters.periodFrom, false),
      periodTo: normalizeDate(filters.periodTo, true),
    };
    if (filters.gstin) params.gstin = filters.gstin;
    return new Promise((resolve, reject) => {
      API.get("/gst/dashboard/export/suppliers", { params, responseType: "blob" })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }
}

export const GstRestS = new GstRestService();
